import router from "./router";
import { ElMessage, ElMessageBox } from "element-plus";
import NProgress from "nprogress";
import "nprogress/nprogress.css";
import { getToken } from "@/modules/huyu-framework/backend/utils/auth";
import {
  findFirstLeaf,
  flattenLeaves,
  getHomePath,
  hasRoutePath,
  normalizeRouteNames,
} from "@/modules/huyu-framework/backend/utils/routeHelper";
import { isHttp } from "@/modules/huyu-framework/backend/utils/validate";
import { isRelogin } from "@/modules/huyu-framework/backend/utils/request";
import useUserStore from "@/store/modules/user";
import useSettingsStore from "@/store/modules/settings";
import usePermissionStore from "@/store/modules/permission";
import { toRaw } from "vue";

NProgress.configure({ showSpinner: false });

const whiteList = ["/login", "/register"];

/** 命中静态 404 兜底通配（/:pathMatch(.*)*） */
function isNotFoundRoute(to) {
  return (to.matched || []).some((r) => r.path === "/:pathMatch(.*)*");
}

/** 命中已注册但组件未解析（loadView 未命中 => 渲染空白）的叶子 */
function isBlankRoute(to) {
  const last = (to.matched || [])[to.matched ? to.matched.length - 1 : 0];
  if (!last || last.path === "/:pathMatch(.*)*") return false;
  return !last.components || last.components.default == null;
}

/** 路由信息不存在：明确提示后回登录（主人定调——禁止 404 死循环，最后出口必须是登录页提示） */
function routeMissingExit() {
  ElMessageBox.alert(
    "当前路由信息不存在：没有任何可打开的页面。请联系管理员检查菜单配置，或重新登录。",
    "路由信息不存在",
    {
      confirmButtonText: "重新登录",
      callback: () => {
        clearAllClientCache();
        location.href = "/login";
      },
    }
  );
}

router.beforeEach((to, from, next) => {
  NProgress.start();
  if (getToken()) {
    to.meta.title && useSettingsStore().setTitle(to.meta.title);
    /* has token*/
    if (to.path === "/login") {
      const sidebar = usePermissionStore().sidebarRouters;
      const home = getHomePath(sidebar);
      if (home) {
        next({ path: home });
      } else if (sidebar.length) {
        // 缺「首页」（含外链首页被忽略跳过）→ 静默降级到首个可渲染页，不弹红错
        const first = findFirstLeaf(sidebar);
        if (first) {
          console.warn("[守卫] 未找到可渲染的「首页」菜单，静默落地:", first.path);
          next({ path: first.path });
        } else {
          // 一个可渲染页都没有 → 登录页提示「路由信息不存在」，禁止 404 死循环
          routeMissingExit();
          next(false);
        }
      } else {
        // 首次生成前：交由下方生成逻辑计算落地路径
        next({ path: "/" });
      }
      NProgress.done();
    } else if (whiteList.indexOf(to.path) !== -1) {
      next();
    } else {
      // console.log(" 根据roles权限生成可访问的路由表", to);

      // 站内根路径 "/"：动态映射到当前用户首页（路由体系全动态，不设静态 /index）
      if (to.path === "/") {
        const sidebar = usePermissionStore().sidebarRouters;
        const home = getHomePath(sidebar);
        if (home) {
          next({ path: home, replace: true });
          NProgress.done();
          return;
        }
        if (sidebar.length) {
          // 缺「首页」（含外链首页被忽略跳过）→ 静默降级到首个可渲染页，不弹红错
          const first = findFirstLeaf(sidebar);
          if (first) {
            console.warn("[守卫] 未找到可渲染的「首页」菜单，静默落地:", first.path);
            next({ path: first.path, replace: true });
            NProgress.done();
            return;
          }
          // 一个可渲染页都没有 → 提示「路由信息不存在」回登录，禁止 404 死循环
          routeMissingExit();
          next(false);
          NProgress.done();
          return;
        }
        // sidebar 为空且未生成 → 落到下方生成分支计算路径
      }

      if (useUserStore().roles.length === 0) {
        isRelogin.show = true;
        // 判断当前用户是否已拉取完user_info信息
        useUserStore()
          .getInfo()
          .then(() => {
            isRelogin.show = false;
            return usePermissionStore()
              .generateRoutes()
              .then((accessRoutes) => {
                // 根据roles权限生成可访问的路由表
                // store 中注册的是硬编码动态路由(asyncRoutes)，这里注册 API 返回的路由(rewriteRoutes)

                // 路由名补全/查重：顶层包装路由 name 常为空串 → 派生唯一名；
                // 撞名只影响该条命名并显式报错，绝不中断整棵树的注册（2026-10-20 修复登录卡死）
                const nameConflicts = normalizeRouteNames(
                  accessRoutes,
                  router.getRoutes().map((r) => r.name).filter(Boolean)
                );
                if (nameConflicts.length) {
                  console.error("[守卫] 路由名重复，请修正菜单配置", nameConflicts);
                  ElMessage.error(
                    `路由名重复：${nameConflicts
                      .map((c) => c.path)
                      .join("、")}，请修正菜单配置`
                  );
                }

                const failedPaths = new Set();
                accessRoutes.forEach((route) => {
                  if (!isHttp(route.path)) {
                    try {
                      router.addRoute(route); // 动态添加可访问路由表
                    } catch (error) {
                      // 暴露：注册失败必须让用户看见（§2.6 禁止静默丢路由）；
                      // 记录该子树全部叶子路径，落地时跳过（不落向未注册路由）
                      flattenLeaves([route]).forEach((leaf) =>
                        failedPaths.add(leaf.path)
                      );
                      failedPaths.add(route.path);
                      console.error(`[守卫] 路由注册失败: ${route.path}`, error);
                      ElMessage.error(
                        `路由注册失败：${route.path}（${error.message}）`
                      );
                    }
                  }
                });

                // 动态落地路径：取首个可渲染叶子（跳过注册失败的子树 + 组件未命中断链 + 外链）
                const firstLeaf = flattenLeaves(accessRoutes).find(
                  (leaf) => !failedPaths.has(leaf.path) && leaf.componentOk !== false
                );
                if (!firstLeaf) {
                  // 一个可渲染页都没有 → 提示「路由信息不存在」回登录，禁止放行落 404 死循环
                  console.error(
                    "[守卫] 动态路由树中没有任何可渲染的页面，请检查 getRouters 返回数据"
                  );
                  routeMissingExit();
                  next(false);
                  NProgress.done();
                  return;
                }
                const path = firstLeaf.path;
                let query = {};
                if (firstLeaf.query) {
                  try {
                    query = JSON.parse(firstLeaf.query);
                  } catch (e) {
                    // 暴露：菜单 query 配置非法，报出具体菜单与原始值
                    throw new Error(
                      `[守卫] 菜单「${firstLeaf.title}」的 query 不是合法 JSON：${firstLeaf.query}`
                    );
                  }
                }

                // 先记录本次停留路由（在 next 之前完成：此后若有异常也不会造成二次导航）
                sessionStorage.setItem(
                  "router",
                  JSON.stringify({
                    path: to.path,
                    title: to.meta.title,
                    query: to.query,
                  }),
                );

                var temp = { ...to, replace: true };
                var curPath = temp.path;

                // console.log(' 根据roles权限生成可访问的路由表', temp, path);
                // if (from.path == "/login" || curPath == "/") {
                //   next({ path, replace: true }); // hack方法 确保addRoutes已完成
                // } else {
                // next({ path, replace: true }) // hack方法 确保addRoutes已完成

                // 恢复上次停留路由：按「路径是否仍存在于当前动态路由树」校验
                // （旧实现按 title 匹配后按旧 name 跳转——切换项目/菜单调整后旧 name 未注册 → 404）
                let routerss = null;
                const rawRouter = sessionStorage.getItem("router");
                if (rawRouter) {
                  try {
                    routerss = JSON.parse(rawRouter);
                  } catch (e) {
                    // 损坏数据：清除避免反复炸，并抛错暴露（不静默当"没有记录"处理）
                    sessionStorage.removeItem("router");
                    throw new Error(
                      `[守卫] sessionStorage[router] 已损坏并清除：${e.message}`
                    );
                  }
                }

                if (routerss && hasRoutePath(accessRoutes, routerss.path)) {
                  next({
                    path: routerss.path,
                    replace: true,
                    query: routerss.query || {},
                  });
                } else {
                  next({ path, replace: true, query });
                }

                // hack方法 确保addRoutes已完成
                // }
              })
              .catch((routeErr) => {
                // 【错误分层】路由数据/配置类错误：会话保留、绝不跳登录——
                // 但禁止直接放行落 404 死循环：先兜底到首个可渲染页，兜不住才回登录提示
                console.error("[守卫] 路由数据/配置异常", routeErr);
                ElMessage.error(routeErr?.message || "路由数据异常");
                const fallback = findFirstLeaf(
                  usePermissionStore().sidebarRouters
                );
                if (fallback && fallback.path !== to.path) {
                  next({ path: fallback.path, replace: true });
                  NProgress.done();
                } else {
                  routeMissingExit();
                  next(false);
                  NProgress.done();
                }
              });
          })
          .catch((err) => {
            // 【错误分层】getInfo 身份/认证类失败 → 登录页属正常行为
            useUserStore()
              .logOut()
              .then(() => {
                ElMessage.error(err);
                next({ path: "/" });
              });
          });
      } else {
        // console.log(" 根据roles权限生成可访问的路由表", to);

        // ===== 防线C：禁止 404 死循环（主人定调） =====
        // 命中静态 404 通配、或已注册但组件未命中的空白页 → 自救到可渲染页；
        // 计数上限 3 次，超限视为「路由信息不存在」→ 提示后回登录，绝不反复跳。
        if (isNotFoundRoute(to) || isBlankRoute(to)) {
          const hits =
            parseInt(sessionStorage.getItem("route404Hits") || "0", 10) + 1;
          sessionStorage.setItem("route404Hits", String(hits));
          const sidebar = usePermissionStore().sidebarRouters;
          const target = getHomePath(sidebar) || findFirstLeaf(sidebar);
          if (target && target !== to.path && hits <= 3) {
            ElMessage.warning(
              `当前页面不存在或不可用，已自动跳转到可用页面（第 ${hits} 次兜底）`
            );
            next({ path: target, replace: true });
            NProgress.done();
            return;
          }
          // 兜不住 / 超限：路由信息不存在 → 提示后回登录（唯一出口，禁止循环）
          routeMissingExit();
          next(false);
          NProgress.done();
          return;
        }
        sessionStorage.removeItem("route404Hits");

        sessionStorage.setItem(
          "router",
          JSON.stringify({
            path: to.path,
            name: to.name,
            title: to.meta.title,
            query: to.query,
          }),
        );

        next();
      }
    }
  } else {
    // 没有token
    if (whiteList.indexOf(to.path) !== -1) {
      // 在免登录白名单，直接进入
      next();
    } else {
      next(`/login?redirect=${to.fullPath}`); // 否则全部重定向到登录页
      NProgress.done();
    }
  }
});

router.afterEach(() => {
  NProgress.done();
});
const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key, value) => {
    if (typeof value === "object" && value !== null) {
      if (seen.has(value)) {
        return; // 遇到循环引用时返回 undefined
      }
      seen.add(value);
    }
    return value;
  };
};

